import React, { useState, useEffect } from 'react';
import { sharedStyles, getStatusColor, formatDate, formatCurrency } from '../styles/sharedStyles';
import { useApi } from '../hooks/useApi';
import { useUser } from '../hooks/useUser';

// Dummy data for employees
const dummyEmployees = [
  { id: 1, name: 'John Smith', position: 'Excavator Operator', phone: '555-0123', email: 'john.smith@email.com', status: 'Active', pin: '1234' },
  { id: 2, name: 'Sarah Johnson', position: 'Site Manager', phone: '555-0124', email: 'sarah.j@email.com', status: 'Active', pin: '2345' },
  { id: 3, name: 'Mike Brown', position: 'Crane Operator', phone: '555-0125', email: 'mike.b@email.com', status: 'On Leave', pin: '3456' },
  { id: 4, name: 'Lisa Davis', position: 'Safety Officer', phone: '555-0126', email: 'lisa.d@email.com', status: 'Active', pin: '4567' },
  { id: 5, name: 'Tom Wilson', position: 'Bulldozer Operator', phone: '555-0127', email: 'tom.w@email.com', status: 'Active', pin: '5678' },
  { id: 6, name: 'Emma Taylor', position: 'Project Coordinator', phone: '555-0128', email: 'emma.t@email.com', status: 'Active', pin: '6789' },
  { id: 7, name: 'James Anderson', position: 'Truck Driver', phone: '555-0129', email: 'james.a@email.com', status: 'On Leave', pin: '7890' },
  { id: 8, name: 'Rachel Martinez', position: 'Equipment Manager', phone: '555-0130', email: 'rachel.m@email.com', status: 'Active', pin: '8901' },
  { id: 9, name: 'David Thompson', position: 'Loader Operator', phone: '555-0131', email: 'david.t@email.com', status: 'Active', pin: '9012' },
  { id: 10, name: 'Sophia Garcia', position: 'Site Engineer', phone: '555-0132', email: 'sophia.g@email.com', status: 'Active', pin: '0123' },
];

const Employees = () => {
  const api = useApi();
  const { user } = useUser();
  const canManage = user?.role === 'admin';
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('Active'); 

  const [formData, setFormData] = useState({
    name: '',
    position: '',
    email: '',
    phone: '',
    status: 'Active',
    joinDate: '',
    department: '',
    projects: '',
    pincode: ''
  });

  // Fetch employees with pagination and filters
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...(selectedDepartment && { department: selectedDepartment }),
        ...(selectedStatus && { status: selectedStatus })
      });

      const response = await api({
        method: 'get',
        url: `/employees?${params}`
      });
      
      if (response.data.success) {
        setEmployees(response.data.data.employees);
        setTotalPages(response.data.data.pagination.totalPages);
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError(err.response?.data?.message || 'Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [currentPage, searchTerm, selectedDepartment, selectedStatus]);

  // Add effect to handle body scroll when modal is open
  useEffect(() => {
    if (showModal || showEditModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal, showEditModal]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      // Convert empty email to null
      const formDataToSubmit = {
        ...formData,
        email: formData.email === '' ? null : formData.email,
        projects: parseInt(formData.projects) || 0
      };

      console.log('Submitting employee data:', formDataToSubmit);
      
      const response = await api({
        method: 'post',
        url: `/employees`,
        data: formDataToSubmit
      });
      
      if (response.data.success) {
        await fetchEmployees();
        handleCloseModal();
      }
    } catch (err) {
      console.error('Error creating employee:', err);
      setError(err.response?.data?.message || 'Failed to create employee');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id, updatedData) => {
    try {
      setLoading(true);
      const response = await api({
        method: 'put',
        url: `/employees/${id}`,
        data: updatedData
      });
      
      if (response.data.success) {
        fetchEmployees();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update employee');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this operator?')) {
      try {
        setLoading(true);
        const response = await api({
          method: 'delete',
          url: `/employees/${id}`
        });
        
        if (response.data.success) {
          fetchEmployees();
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete employee');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      name: '',
      position: '',
      email: '',
      phone: '',
      status: 'Active',
      joinDate: '',
      department: '',
      projects: '',
      pincode: ''
    });
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name || '',
      position: employee.position || '',
      email: employee.email || '',
      phone: employee.phone || '',
      status: employee.status || 'Active',
      joinDate: employee.joinDate ? employee.joinDate.split('T')[0] : '',
      department: employee.department || '',
      projects: employee.projects || '',
      pincode: employee.pincode || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      const formDataToSubmit = {
        ...formData,
        projects: parseInt(formData.projects) || 0
      };

      const response = await api({
        method: 'put',
        url: `/employees/${editingEmployee.id}`,
        data: formDataToSubmit
      });
      
      if (response.data.success) {
        await fetchEmployees();
        handleCloseEditModal();
      }
    } catch (err) {
      console.error('Error updating employee:', err);
      setError(err.response?.data?.message || 'Failed to update employee');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingEmployee(null);
    setFormData({
      name: '',
      position: '',
      email: '',
      phone: '',
      status: 'Active',
      joinDate: '',
      department: '',
      projects: '',
      pincode: ''
    });
  };

  return (
    <div style={sharedStyles.pageContainer} className="container-fluid px-2 px-md-4 py-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
        <h2 style={sharedStyles.pageTitle}>Operator Management</h2>
        {canManage && (
          <button 
            className="btn"
            style={sharedStyles.button.primary}
            onClick={() => setShowModal(true)}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Add Operator
          </button>
        )}
      </div>

      {/* Search and Filter Section */}
      <div className="card mb-4" style={sharedStyles.card}>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, email, or pincode"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                <option value="">All Departments</option>
                <option value="Laborers">Laborers</option>
                <option value="Mechanics">Mechanics</option>
                <option value="Drivers">Drivers</option>
                <option value="Operators">Operators</option>
                <option value="Manager">Manager</option>
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="On Leave">On Leave</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center my-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Employees Table */}
      <div className="card" style={sharedStyles.card}>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table align-middle mb-0" style={sharedStyles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>PIN Code</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(employee => {
                  const statusStyle = getStatusColor(employee.status);
                  return (
                    <tr key={employee.id}>
                      <td>
                        <div style={{ fontWeight: '500', color: sharedStyles.colors.text.primary }}>
                          {employee.name}
                        </div>
                      </td>
                      <td>{employee.phone}</td>
                      <td>
                        <div style={{ 
                          fontFamily: 'monospace',
                          backgroundColor: sharedStyles.colors.background.secondary,
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          color: sharedStyles.colors.text.secondary
                        }}>
                          {employee.pincode}
                        </div>
                      </td>
                      <td>
                        <div style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '1rem',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          backgroundColor: statusStyle.background,
                          color: statusStyle.text
                        }}>
                          {employee.status}
                        </div>
                      </td>
                      <td>
                        {canManage ? (
                          <div className="btn-group">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEdit(employee)}
                              title="Edit Operator"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => handleUpdate(employee.id, { ...employee, status: 'Active' })}
                              disabled={employee.status === 'Active'}
                              title="Activate Operator"
                            >
                              <i className="bi bi-check-circle"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(employee.id)}
                              title="Delete Operator"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        ) : (
                          <span className="text-muted small">View only</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <nav className="mt-4" style={sharedStyles.pagination} aria-label="Employee table pagination">
            <ul className="pagination justify-content-center mb-0 flex-wrap">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => setCurrentPage(prev => prev - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>
              {[...Array(totalPages)].map((_, index) => (
                <li key={index + 1} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                  <button 
                    className="page-link"
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button 
                  className="page-link"
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Add Employee Modal */}
      {showModal && (
        <>
          <div 
            className="modal-backdrop fade show" 
            style={sharedStyles.modal.overlay}
            onClick={handleCloseModal}
          ></div>
          <div 
            className="modal fade show" 
            style={{ 
              display: 'block',
              zIndex: 1500
            }} 
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content" style={sharedStyles.modal.content}>
                <div className="modal-header border-0 pb-0">
                  <h5 style={sharedStyles.cardTitle}>Add New Operator</h5>
                  <button 
                    type="button" 
                    className="btn-close"
                    onClick={handleCloseModal}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Name</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Position</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="position"
                          value={formData.position}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Email</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Phone</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Department</label>
                        <select
                          className="form-select"
                          style={sharedStyles.formSelect}
                          name="department"
                          value={formData.department}
                          onChange={handleInputChange}
                        >
                          <option value="Laborers">Laborers</option>
                          <option value="Mechanics">Mechanics</option>
                          <option value="Drivers">Drivers</option>
                          <option value="Operators">Operators</option>
                          <option value="Manager">Manager</option>
                        </select>
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Status</label>
                        <select
                          className="form-select"
                          style={sharedStyles.formSelect}
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                          <option value="On Leave">On Leave</option>
                        </select>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Join Date</label>
                        <input
                          type="date"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="joinDate"
                          value={formData.joinDate}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>PIN</label>
                        <input
                          type="password"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleInputChange}
                          placeholder="Enter 4-digit PIN"
                          maxLength="4"
                          pattern="[0-9]{4}"
                          required
                        />
                        {formData.pincode && !/^[0-9]{4}$/.test(formData.pincode) && (
                          <div style={{ color: 'red', fontSize: '0.9em' }}>
                            PIN must be exactly 4 digits.
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="text-end mt-4">
                      <button 
                        type="button" 
                        className="btn me-2"
                        style={sharedStyles.button.secondary}
                        onClick={handleCloseModal}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="btn"
                        style={sharedStyles.button.primary}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Adding...
                          </>
                        ) : (
                          'Add Operator'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit Employee Modal */}
      {showEditModal && (
        <>
          <div 
            className="modal-backdrop fade show" 
            style={sharedStyles.modal.overlay}
            onClick={handleCloseEditModal}
          ></div>
          <div 
            className="modal fade show" 
            style={{ 
              display: 'block',
              zIndex: 1500
            }} 
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content" style={sharedStyles.modal.content}>
                <div className="modal-header border-0 pb-0">
                  <h5 style={sharedStyles.cardTitle}>Edit Operator</h5>
                  <button 
                    type="button" 
                    className="btn-close"
                    onClick={handleCloseEditModal}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleUpdateSubmit}>
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Name</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Position</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="position"
                          value={formData.position}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Email</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Phone</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Department</label>
                        <select
                          className="form-select"
                          style={sharedStyles.formSelect}
                          name="department"
                          value={formData.department}
                          onChange={handleInputChange}
                        >
                          <option value="Laborers">Laborers</option>
                          <option value="Mechanics">Mechanics</option>
                          <option value="Drivers">Drivers</option>
                          <option value="Operators">Operators</option>
                          <option value="Manager">Manager</option>
                        </select>
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Status</label>
                        <select
                          className="form-select"
                          style={sharedStyles.formSelect}
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                          <option value="On Leave">On Leave</option>
                        </select>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Join Date</label>
                        <input
                          type="date"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="joinDate"
                          value={formData.joinDate}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>PIN</label>
                        <input
                          type="password"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleInputChange}
                          placeholder="Enter 4-digit PIN"
                          maxLength="4"
                          pattern="[0-9]{4}"
                          required
                        />
                        {formData.pincode && !/^[0-9]{4}$/.test(formData.pincode) && (
                          <div style={{ color: 'red', fontSize: '0.9em' }}>
                            PIN must be exactly 4 digits.
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="modal-footer border-0 pt-0">
                      <button 
                        type="button" 
                        className="btn me-2"
                        style={sharedStyles.button.secondary}
                        onClick={handleCloseEditModal}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="btn"
                        style={sharedStyles.button.primary}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Updating...
                          </>
                        ) : (
                          'Update Operator'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Employees; 